import { Injectable } from '@angular/core';
import { PermissionType } from '@app/core/models/permission';
import { IUser } from '@app/core/models/user';
import { AuthService } from '@app/core/services/auth.service';
import { Action, State, StateContext } from '@ngxs/store';
import { switchMap } from 'rxjs/operators';
import {
  FetchLoggedInUser,
  Login,
  Logout,
  ResetPassword,
  SendResetPasswordLink,
  SetLastLoginUsername,
  SetRememberMe,
  SetToken,
  SetUser,
  RefreshToken,
} from './session.actions';

export interface ISessionState {
  user: IUser;
  token: string;
  refresh_token: string;
  rememberLoginUsername: boolean;
  lastLoginUsername: string;
  permissions: PermissionType[];
}

const initStates: ISessionState = {
  user: null,
  token: '',
  refresh_token: '',
  rememberLoginUsername: false,
  lastLoginUsername: '',
  permissions: [],
};

@State<ISessionState>({
  name: 'session',
  defaults: initStates,
})
@Injectable()
export class SessionState {
  constructor(private authService: AuthService) {}

  @Action(Login)
  login(ctx: StateContext<ISessionState>, action: Login) {
    return this.authService
      .login$(action.username, action.password)
      .pipe(
        switchMap(({ token, refresh_token }) =>
          ctx.dispatch([new SetToken(token, refresh_token), new FetchLoggedInUser()]),
        ),
      );
  }

  @Action(RefreshToken)
  refreshToken(ctx: StateContext<ISessionState>, action: RefreshToken) {
    return this.authService
      .refreshToken$(action.token)
      .pipe(switchMap(({ token, refresh_token }) => ctx.dispatch(new SetToken(token, refresh_token))));
  }

  @Action(FetchLoggedInUser)
  FetchLoggedInUser(ctx: StateContext<ISessionState>) {
    return this.authService.me$().pipe(switchMap(user => ctx.dispatch(new SetUser(user as IUser))));
  }

  @Action(Logout)
  logout(ctx: StateContext<ISessionState>) {
    return ctx.dispatch([new SetToken('', ''), new SetUser(null)]);
  }

  @Action(SetToken)
  SetToken(ctx: StateContext<ISessionState>, action: SetToken) {
    const { token, refresh_token } = action;
    let nextState: any = { token };
    if (action.refresh_token) {
      nextState = { ...nextState, refresh_token };
    }
    ctx.patchState(nextState);
  }

  @Action(SetUser)
  setUser(ctx: StateContext<ISessionState>, action: SetUser) {
    const { user } = action;
    if (user !== null) {
      const permissions = user.roleEntities.reduce((pre, role) => {
        return [...pre, ...role.permissions];
      }, []);
      ctx.patchState({ user, permissions });
    } else {
      ctx.patchState({ user: null, permissions: [] });
    }
  }

  @Action(ResetPassword)
  resetPassword(ctx: StateContext<ISessionState>, action: ResetPassword) {
    const { payload } = action;
    return this.authService.resetPassword$(payload);
  }

  @Action(SendResetPasswordLink)
  sendResetPasswordLink(ctx: StateContext<ISessionState>, action: SendResetPasswordLink) {
    return this.authService.sendResetPasswordLink$(action.email);
  }

  @Action(SetRememberMe)
  setRememberMe(ctx: StateContext<ISessionState>, action: SetRememberMe) {
    ctx.patchState({
      rememberLoginUsername: action.rememberMe,
    });
  }

  @Action(SetLastLoginUsername)
  setLastLoginUsername(ctx: StateContext<ISessionState>, action: SetLastLoginUsername) {
    ctx.patchState({
      lastLoginUsername: action.lastUsername,
    });
  }
}
